import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, OnDestroy, SimpleChanges, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';

import { ComissaoConvocacaoContextoResponse, ComissaoConvocacaoEmailParticipanteResponse, ComissaoConvocacaoParticipanteResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { ComissaoReuniaoService } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.service';
import { ReuniaoComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SendWhatsapp } from '@/shared/components/send-whatsapp/send-whatsapp';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type CodigoPresencaConvocacao = 'P' | 'V' | 'A' | 'J' | '';

@Component({
    selector: 'app-gerenciar-convocacoes',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, FormsModule, ButtonModule, DialogModule, InputTextModule, PesquisarPessoaCustomComponent, CardContent, SendWhatsapp, CanDirective],
    templateUrl: './gerenciar-convocacoes.html',
    styleUrls: ['./gerenciar-convocacoes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GerenciarConvocacoesComponent implements OnChanges, OnDestroy {
    private readonly reuniaoService = inject(ComissaoReuniaoService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly cdr = inject(ChangeDetectorRef);

    protected readonly roleEditar = PERMISSOES.comissoes.convocacoes.editar;
    protected readonly roleRemoverConvidado = PERMISSOES.comissoes.convocacoes.removerConvidado;
    protected readonly roleEnviarEmail = PERMISSOES.comissoes.convocacoes.enviarEmail;

    /** Visibilidade do modal (two-way `[(visivel)]`). */
    readonly visivel = model(false);
    /** Reunião cujas convocações são gerenciadas. */
    readonly reuniao = input<ReuniaoComissao | null>(null);
    /** Emite ao atualizar (convocação/presença) para a lista recarregar. */
    readonly atualizado = output<void>();

    carregando = false;
    salvando = false;
    mensagem = '';
    erro = '';
    contexto: ComissaoConvocacaoContextoResponse = {};
    participantes: ComissaoConvocacaoParticipanteResponse[] = [];
    dtConvocacao = '';
    dtAtoConcessao = '';
    dsObservacao = '';
    pesquisaConvidadoVisivel = false;
    historicoEmails: ComissaoConvocacaoEmailParticipanteResponse[] = [];
    historicoEmailsVisivel = false;
    acaoMarcarTodos: 'marcar' | 'desmarcar' | null = null;
    destinatariosEmailSelecionados = new Set<string>();

    private consultaSub?: Subscription;
    private acaoSub?: Subscription;
    private historicoSub?: Subscription;
    private oficioSub?: Subscription;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visivel'] && this.visivel()) {
            this.carregar();
        }
    }

    ngOnDestroy(): void {
        this.consultaSub?.unsubscribe();
        this.acaoSub?.unsubscribe();
        this.historicoSub?.unsubscribe();
        this.oficioSub?.unsubscribe();
    }

    get titulo(): string {
        const reuniao = this.reuniao();
        return reuniao ? `Convocações da reunião ${reuniao.codigo}` : 'Convocações';
    }

    get permiteAlteracao(): boolean {
        return this.contexto.idStatusReuniao === 1 || this.contexto.idStatusReuniao === 2;
    }

    get todosMarcados(): boolean {
        const convocaveis = this.participantes.filter((item) => this.idSolicitante(item));
        return convocaveis.length > 0 && convocaveis.every((item) => item.inAtoConcessao === 'S');
    }

    get rotuloMarcarTodos(): string {
        if (this.acaoMarcarTodos === 'marcar') {
            return 'Marcando...';
        }
        if (this.acaoMarcarTodos === 'desmarcar') {
            return 'Desmarcando...';
        }
        return this.todosMarcados ? 'Desmarcar todos' : 'Marcar todos';
    }

    get iconeMarcarTodos(): string {
        return this.acaoMarcarTodos ? 'pi pi-spin pi-spinner' : 'pi pi-check-square';
    }

    get totalDestinatariosEmailSelecionados(): number {
        return this.destinatariosEmailSelecionados.size;
    }

    get todosDestinatariosEmailSelecionados(): boolean {
        const destinatarios = this.participantes.filter((item) => this.idSolicitante(item) && this.ufSolicitante(item));
        return destinatarios.length > 0 && destinatarios.every((item) => this.destinatariosEmailSelecionados.has(this.chaveDestinatarioEmail(item)));
    }

    get rotuloSelecionarEmails(): string {
        return this.todosDestinatariosEmailSelecionados ? 'Desmarcar e-mails' : 'Marcar e-mails';
    }

    get totalParticipantes(): number {
        return this.participantes.length;
    }

    get totalEmailsEnviados(): number {
        return this.participantes.filter((participante) => participante.inStatusEnvioEmailOk === 'S').length;
    }

    get totalEmailsComErro(): number {
        return this.participantes.filter((participante) => participante.inStatusEnvioEmailErro === 'N').length;
    }

    get totalEmailsPendentes(): number {
        return Math.max(this.totalParticipantes - this.totalEmailsEnviados - this.totalEmailsComErro, 0);
    }

    get totalPresencasConfirmadas(): number {
        return this.participantes.filter((participante) => participante.inPresenca === 'P' || participante.inPresenca === 'V').length;
    }

    fechar(): void {
        this.visivel.set(false);
    }

    carregar(): void {
        const idReuniao = this.idReuniaoAtual();
        if (!idReuniao) {
            return;
        }

        this.carregando = true;
        this.erro = '';
        this.mensagem = '';
        this.consultaSub?.unsubscribe();
        this.consultaSub = this.reuniaoService.consultarConvocacoes(idReuniao).subscribe({
            next: (response) => {
                this.contexto = response.contexto ?? {};
                this.participantes = response.participantes ?? [];
                this.sincronizarSelecaoEmail();
                this.dtConvocacao = this.formatarDataInput(this.contexto.dtConvocacao);
                this.dtAtoConcessao = this.formatarDataInput(this.contexto.dtAtoConcessao);
                this.dsObservacao = this.contexto.dsObservacao ?? '';
                if (this.historicoEmailsVisivel) {
                    this.carregarHistoricoEmails();
                }
                this.carregando = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.erro = 'Não foi possível carregar as convocações.';
                this.carregando = false;
                this.cdr.markForCheck();
            }
        });
    }

    salvarContexto(): void {
        const idReuniao = this.idReuniaoAtual();
        if (!idReuniao || !this.permiteAlteracao) {
            return;
        }

        this.executarAcao(
            this.reuniaoService.salvarContextoConvocacao(idReuniao, {
                dtConvocacao: this.dtConvocacao || null,
                dtAtoConcessao: this.dtAtoConcessao || null,
                dsObservacao: this.dsObservacao || null
            }),
            'Dados da convocação salvos.'
        );
    }

    alternarAto(participante: ComissaoConvocacaoParticipanteResponse): void {
        const valor = participante.inAtoConcessao === 'S' ? 'N' : 'S';
        if (valor === 'N' && participante.inAtoConcessao === 'S' && participante.inEmissaoPassagem === 'S') {
            this.confirmationService.confirm({
                header: 'Remover ato de concessão',
                message: 'Ao remover o ato de concessão a passagem será removida. Deseja continuar?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim',
                rejectLabel: 'Não',
                acceptButtonStyleClass: 'p-button-danger',
                rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
                accept: () => this.atualizarParticipante(participante, { inAtoConcessao: valor, inEmissaoPassagem: 'N' })
            });
            return;
        }
        this.atualizarParticipante(participante, { inAtoConcessao: valor, inEmissaoPassagem: valor === 'N' ? 'N' : participante.inEmissaoPassagem });
    }

    alternarPassagem(participante: ComissaoConvocacaoParticipanteResponse): void {
        if (participante.inAtoConcessao !== 'S') {
            this.erro = 'Não e possível marcar passagem sem ato de concessão.';
            return;
        }
        this.atualizarParticipante(participante, { inEmissaoPassagem: participante.inEmissaoPassagem === 'S' ? 'N' : 'S' });
    }

    alterarPresenca(participante: ComissaoConvocacaoParticipanteResponse, presenca: CodigoPresencaConvocacao): void {
        if (!presenca) {
            this.atualizarParticipante(participante, { inConfirmacao: null, inPresenca: null, idMeioConfirmacao: null });
            return;
        }
        this.atualizarParticipante(participante, {
            inConfirmacao: presenca === 'P' || presenca === 'V' ? 'S' : 'N',
            inPresenca: presenca,
            idMeioConfirmacao: participante.idMeioConfirmacao ?? 1
        });
    }

    marcarTodos(): void {
        const idReuniao = this.idReuniaoAtual();
        if (!idReuniao || !this.permiteAlteracao || this.acaoMarcarTodos) {
            return;
        }

        const marcar = !this.todosMarcados;
        const inAtoConcessao = marcar ? 'S' : 'N';
        this.salvando = true;
        this.acaoMarcarTodos = marcar ? 'marcar' : 'desmarcar';
        this.erro = '';
        this.mensagem = marcar ? 'Marcando atos de concessão...' : 'Desmarcando atos de concessão...';

        this.acaoSub?.unsubscribe();
        this.acaoSub = this.reuniaoService.marcarTodosConvocacao(idReuniao, { inAtoConcessao }).subscribe({
            next: () => {
                this.participantes = this.participantes.map((participante) => ({
                    ...participante,
                    idSolicitante: participante.idSolicitante ?? participante.idMembroComissao,
                    ufSgUf: participante.ufSgUf ?? participante.ufMembroComissao,
                    inAtoConcessao
                }));
                this.mensagem = marcar ? 'Atos de concessão marcados.' : 'Atos de concessão removidos.';
                this.salvando = false;
                this.acaoMarcarTodos = null;
                this.atualizado.emit();
                this.cdr.markForCheck();
            },
            error: () => {
                this.erro = 'Não foi possível atualizar a convocação.';
                this.mensagem = '';
                this.salvando = false;
                this.acaoMarcarTodos = null;
                this.cdr.markForCheck();
            }
        });
    }

    removerConvidado(participante: ComissaoConvocacaoParticipanteResponse): void {
        const idReuniao = this.idReuniaoAtual();
        const idSolicitante = this.idSolicitante(participante);
        const ufSgUf = this.ufSolicitante(participante);
        if (!idReuniao || !idSolicitante || !ufSgUf || participante.idParticipacao !== 4 || !this.permiteAlteracao) {
            return;
        }

        this.executarAcao(this.reuniaoService.removerConvidadoConvocacao(idReuniao, idSolicitante, ufSgUf), 'Convidado removido.');
    }

    enviarEmails(): void {
        const idReuniao = this.idReuniaoAtual();
        if (!idReuniao || !this.permiteAlteracao) {
            return;
        }

        const destinatarios = this.participantes
            .filter((participante) => this.destinatariosEmailSelecionados.has(this.chaveDestinatarioEmail(participante)))
            .map((participante) => ({
                idSolicitante: this.idSolicitante(participante) ?? 0,
                ufSgUf: this.ufSolicitante(participante),
                nmEmail: participante.nmEmail ?? null
            }))
            .filter((item) => item.idSolicitante > 0 && item.ufSgUf);

        if (!destinatarios.length) {
            this.erro = 'Selecione ao menos um participante para envio de e-mail.';
            return;
        }

        this.salvando = true;
        this.erro = '';
        this.mensagem = '';
        this.acaoSub?.unsubscribe();
        this.acaoSub = this.reuniaoService.enviarEmailsConvocacao(idReuniao, { enviarTodos: false, destinatarios }).subscribe({
            next: (response) => {
                const enviados = response.totalEnviados ?? 0;
                const erros = response.totalErros ?? 0;
                this.mensagem = `Envio processado. Enviados: ${enviados}. Erros: ${erros}.`;
                this.salvando = false;
                this.atualizado.emit();
                this.carregar();
                this.carregarHistoricoEmails();
                this.cdr.markForCheck();
            },
            error: () => {
                this.erro = 'Não foi possível enviar os e-mails de convocação.';
                this.salvando = false;
                this.cdr.markForCheck();
            }
        });
    }

    alternarDestinatarioEmail(participante: ComissaoConvocacaoParticipanteResponse, selecionado: boolean): void {
        const chave = this.chaveDestinatarioEmail(participante);
        if (!chave) {
            return;
        }
        if (selecionado) {
            this.destinatariosEmailSelecionados.add(chave);
        } else {
            this.destinatariosEmailSelecionados.delete(chave);
        }
    }

    alternarTodosDestinatariosEmail(): void {
        if (this.todosDestinatariosEmailSelecionados) {
            this.destinatariosEmailSelecionados.clear();
            return;
        }
        this.participantes.filter((participante) => this.idSolicitante(participante) && this.ufSolicitante(participante)).forEach((participante) => this.destinatariosEmailSelecionados.add(this.chaveDestinatarioEmail(participante)));
    }

    destinatarioEmailSelecionado(participante: ComissaoConvocacaoParticipanteResponse): boolean {
        return this.destinatariosEmailSelecionados.has(this.chaveDestinatarioEmail(participante));
    }

    destinatarioEmailSelecionavel(participante: ComissaoConvocacaoParticipanteResponse): boolean {
        return !!this.idSolicitante(participante) && !!this.ufSolicitante(participante);
    }

    reenviarEmail(participante: ComissaoConvocacaoParticipanteResponse): void {
        const idReuniao = this.idReuniaoAtual();
        const idSolicitante = this.idSolicitante(participante);
        const ufSgUf = this.ufSolicitante(participante);
        if (!idReuniao || !idSolicitante || !ufSgUf || !this.permiteAlteracao) {
            return;
        }

        this.salvando = true;
        this.erro = '';
        this.mensagem = '';
        this.acaoSub?.unsubscribe();
        this.acaoSub = this.reuniaoService.reenviarEmailConvocacao(idReuniao, idSolicitante, ufSgUf).subscribe({
            next: (response) => {
                const enviados = response.totalEnviados ?? 0;
                this.mensagem = enviados > 0 ? 'E-mail reenviado.' : 'Reenvio processado com erro.';
                this.salvando = false;
                this.atualizado.emit();
                this.carregar();
                this.carregarHistoricoEmails();
                this.cdr.markForCheck();
            },
            error: () => {
                this.erro = 'Não foi possível reenviar o e-mail.';
                this.salvando = false;
                this.cdr.markForCheck();
            }
        });
    }

    baixarOficio(participante: ComissaoConvocacaoParticipanteResponse): void {
        const idReuniao = this.idReuniaoAtual();
        const idSolicitante = this.idSolicitante(participante);
        const ufSgUf = this.ufSolicitante(participante);
        if (!idReuniao || !idSolicitante || !ufSgUf) {
            return;
        }

        this.erro = '';
        this.oficioSub?.unsubscribe();
        this.oficioSub = this.reuniaoService.baixarOficioConvite(idReuniao, idSolicitante, ufSgUf).subscribe({
            next: (blob) => this.salvarArquivoOficio(blob, idSolicitante, ufSgUf),
            error: () => {
                this.erro = 'Não foi possível baixar o ofício do participante.';
                this.cdr.markForCheck();
            }
        });
    }

    private salvarArquivoOficio(blob: Blob, idSolicitante: number, ufSgUf: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `oficio-convite-${idSolicitante}-${ufSgUf}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    alternarHistoricoEmails(): void {
        this.historicoEmailsVisivel = !this.historicoEmailsVisivel;
        if (this.historicoEmailsVisivel) {
            this.carregarHistoricoEmails();
        }
    }

    statusEmail(participante: ComissaoConvocacaoParticipanteResponse): string {
        if (participante.inStatusEnvioEmailOk === 'S') {
            return participante.dhEnvioEmail ? `Enviado em ${participante.dhEnvioEmail}` : 'Enviado';
        }
        if (participante.inStatusEnvioEmailErro === 'N') {
            return 'Erro no envio';
        }
        return 'Pendente';
    }

    abrirPesquisaConvidado(): void {
        if (!this.permiteAlteracao || this.salvando) {
            return;
        }
        this.pesquisaConvidadoVisivel = true;
    }

    adicionarConvidado(pessoa: PessoaDomain): void {
        const idReuniao = this.idReuniaoAtual();
        const idSolicitante = Number(pessoa?.id);
        const ufSgUf = String(pessoa?.uf ?? '')
            .trim()
            .toUpperCase();
        if (!idReuniao || !Number.isFinite(idSolicitante) || idSolicitante <= 0 || !ufSgUf || !this.permiteAlteracao) {
            this.erro = 'Selecione um solicitante válido para incluir o convidado.';
            return;
        }

        this.executarAcao(
            this.reuniaoService.atualizarParticipanteConvocacao(idReuniao, {
                idSolicitante,
                ufSgUf,
                inAtoConcessao: 'S',
                inEmissaoPassagem: 'N',
                inConfirmacao: null,
                inPresenca: null,
                idMeioConfirmacao: null
            }),
            'Convidado adicionado.'
        );
    }

    rotuloTipo(participante: ComissaoConvocacaoParticipanteResponse): string {
        if (participante.idParticipacao === 3) {
            return 'Convidado';
        }
        if (participante.idParticipacao === 4) {
            return 'Convidado extra';
        }
        return 'Membro';
    }

    rotuloPresenca(valor?: string | null): string {
        switch (valor) {
            case 'P':
                return 'Presente';
            case 'V':
                return 'Pres. virtual';
            case 'A':
                return 'Ausente';
            case 'J':
                return 'Aus. justificada';
            default:
                return 'Pendente';
        }
    }

    gerarMensagemWhatsappConvocacao(participante?: ComissaoConvocacaoParticipanteResponse | null): string {
        const reuniao = this.reuniao();
        const linhas = [
            '\u{1F4E3} *Convocacao de reuniao*',
            participante?.nmSolicitante ? `\u{1F464} Participante: ${participante.nmSolicitante}` : '',
            reuniao?.nomeComissao ? `\u{1F3DB}\uFE0F Comissao/CT: ${reuniao.nomeComissao}` : '',
            reuniao?.codigo ? `\u{1F522} Reuniao: ${reuniao.codigo}` : '',
            reuniao?.dataInicio ? `\u{1F4C5} Data e horario: ${this.formatarDataHoraMensagem(reuniao.dataInicio)}` : '',
            reuniao?.dataInicio && reuniao?.dataTermino ? `\u{1F552} Horario: ${this.formatarPeriodoMensagem(reuniao)}` : '',
            reuniao?.local ? `\u{1F4CD} Local: ${reuniao.local}` : '',
            reuniao?.modalidade ? `\u{1F4BB} Modalidade: ${reuniao.modalidade}` : '',
            reuniao?.protocolo ? `\u{1F9FE} Protocolo: ${reuniao.protocolo}` : '',
            this.dsObservacao ? `\u{1F4DD} Observacoes: ${this.dsObservacao}` : '',
            '',
            '\u{2705} Favor confirmar o recebimento.'
        ];

        return linhas.filter((linha) => linha !== '').join('\n');
    }

    obterTelefoneParticipante(participante: ComissaoConvocacaoParticipanteResponse): string {
        const item = participante as ComissaoConvocacaoParticipanteResponse & {
            telefone?: string | null;
            telefones?: string | null;
            nrTelefone?: string | null;
            nuTelefone?: string | null;
        };

        return item.telefone ?? item.telefones ?? item.nrTelefone ?? item.nuTelefone ?? '';
    }

    private atualizarParticipante(participante: ComissaoConvocacaoParticipanteResponse, alteracoes: Partial<ComissaoConvocacaoParticipanteResponse>): void {
        const idReuniao = this.idReuniaoAtual();
        const idSolicitante = this.idSolicitante(participante);
        const ufSgUf = this.ufSolicitante(participante);
        if (!idReuniao || !idSolicitante || !ufSgUf || !this.permiteAlteracao) {
            return;
        }

        const possuiAlteracao = (campo: keyof ComissaoConvocacaoParticipanteResponse): boolean => Object.prototype.hasOwnProperty.call(alteracoes, campo);

        this.executarAcao(
            this.reuniaoService.atualizarParticipanteConvocacao(idReuniao, {
                idSolicitante,
                ufSgUf,
                inAtoConcessao: possuiAlteracao('inAtoConcessao') ? (alteracoes.inAtoConcessao ?? null) : (participante.inAtoConcessao ?? null),
                inEmissaoPassagem: possuiAlteracao('inEmissaoPassagem') ? (alteracoes.inEmissaoPassagem ?? null) : (participante.inEmissaoPassagem ?? null),
                inConfirmacao: possuiAlteracao('inConfirmacao') ? (alteracoes.inConfirmacao ?? null) : (participante.inConfirmacao ?? null),
                inPresenca: possuiAlteracao('inPresenca') ? (alteracoes.inPresenca ?? null) : (participante.inPresenca ?? null),
                idMeioConfirmacao: possuiAlteracao('idMeioConfirmacao') ? (alteracoes.idMeioConfirmacao ?? null) : (participante.idMeioConfirmacao ?? null)
            }),
            'Convocação atualizada.'
        );
    }

    private executarAcao(operacao: Observable<unknown>, mensagemSucesso: string): void {
        this.salvando = true;
        this.erro = '';
        this.mensagem = '';
        this.acaoSub?.unsubscribe();
        this.acaoSub = operacao.subscribe({
            next: () => {
                this.mensagem = mensagemSucesso;
                this.salvando = false;
                this.atualizado.emit();
                this.carregar();
                this.cdr.markForCheck();
            },
            error: () => {
                this.erro = 'Não foi possível atualizar a convocação.';
                this.salvando = false;
                this.cdr.markForCheck();
            }
        });
    }

    private idReuniaoAtual(): number | null {
        const valor = Number(this.reuniao()?.codigo);
        return Number.isFinite(valor) && valor > 0 ? valor : null;
    }

    private idSolicitante(participante: ComissaoConvocacaoParticipanteResponse): number | null {
        const valor = Number(participante.idMembroComissao ?? participante.idSolicitante);
        return Number.isFinite(valor) && valor > 0 ? valor : null;
    }

    private ufSolicitante(participante: ComissaoConvocacaoParticipanteResponse): string {
        return String(participante.ufMembroComissao ?? participante.ufSgUf ?? '')
            .trim()
            .toUpperCase();
    }

    private chaveDestinatarioEmail(participante: ComissaoConvocacaoParticipanteResponse): string {
        const idSolicitante = this.idSolicitante(participante);
        const ufSgUf = this.ufSolicitante(participante);
        return idSolicitante && ufSgUf ? `${idSolicitante}-${ufSgUf}` : '';
    }

    private sincronizarSelecaoEmail(): void {
        const chavesAtuais = new Set(this.participantes.map((participante) => this.chaveDestinatarioEmail(participante)).filter(Boolean));
        this.destinatariosEmailSelecionados = new Set(Array.from(this.destinatariosEmailSelecionados).filter((chave) => chavesAtuais.has(chave)));
    }

    private carregarHistoricoEmails(): void {
        const idReuniao = this.idReuniaoAtual();
        if (!idReuniao) {
            return;
        }
        this.historicoSub?.unsubscribe();
        this.historicoSub = this.reuniaoService.listarHistoricoEmailConvocacao(idReuniao).subscribe({
            next: (response) => {
                this.historicoEmails = response ?? [];
                this.cdr.markForCheck();
            },
            error: () => {
                this.historicoEmails = [];
                this.cdr.markForCheck();
            }
        });
    }

    private formatarDataInput(valor?: string | null): string {
        if (!valor) {
            return '';
        }
        const data = new Date(valor);
        if (Number.isNaN(data.getTime())) {
            return String(valor).slice(0, 10);
        }
        return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    }

    private formatarDataHoraMensagem(data: Date | null): string {
        if (!data) {
            return '-';
        }

        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(data);
    }

    private formatarPeriodoMensagem(reuniao: ReuniaoComissao): string {
        if (!reuniao.dataInicio || !reuniao.dataTermino) {
            return '-';
        }

        const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return `${hora.format(reuniao.dataInicio)} - ${hora.format(reuniao.dataTermino)}`;
    }
}
